// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Checkbox, FormControlLabel, Grid2, Stack } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import {
  BaseInput,
  BaseSelect,
  FileUpload,
  Loading,
  PriceBookModal,
  UnderDevelopment,
} from '~/components/common';
// Others
import { defaultValuesForm, schema } from './helper';
import { mockDataMaterialUnit } from '~/utils/mockData/material';
import { IBaseOption, IFileUpload } from '~/utils/interface/common';
import {
  compareDataUpdate,
  createFormData,
  formatInputDecimal,
  isEmptyObject,
  removeEmptyObjects,
} from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import useDebounce from '~/utils/hooks/useDebounce';
import { IFormMaterial, IMaterialDetail } from '~/utils/interface/material';
import {
  BACK_TO_PREVIOUS_PAGE,
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE,
  REG_EXP,
} from '~/utils/constants/common';
import { IFormPriceBookName } from '~/utils/interface/priceBook';
import { IQueryGetListCategory } from '~/utils/interface/category';
import { createMaterial, getMaterialDetail, updateMaterial } from '~/thunks/material/materialThunk';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { TypeMasterDataEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './FormMaterial.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormMaterial = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/material', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: materialId } = useParams();

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormMaterial>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  const businessPriceBook = watch('businessPriceBook');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormMaterial>(defaultValuesForm);
  const [isShowPriceBookModal, setIsShowPriceBookModal] = useState<boolean>(false);
  const [dataListCategory, setDataListCategory] = useState<IBaseOption[]>([]);
  const [categorySearchKey, setCategorySearchKey] = useState<string>(EMPTY_STRING);
  const debounceCategorySearchKey = useDebounce(categorySearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoadingCategory, setIsLoadingCategory] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetMaterialDetail = useCallback(
    (materialId: string) => {
      if (!materialId) return;

      setIsLoading(true);

      dispatch(getMaterialDetail(materialId))
        .unwrap()
        .then((res) => {
          const materialDetail: IMaterialDetail = res?.data;

          const newData: IFormMaterial = {
            name: materialDetail?.name || EMPTY_STRING,
            price: materialDetail?.price ? String(materialDetail?.price) : EMPTY_STRING,
            quantity: materialDetail?.quantity ? String(materialDetail?.quantity) : EMPTY_STRING,
            unit: materialDetail?.unit || EMPTY_STRING,
            description: materialDetail?.description || EMPTY_STRING,
            partNumber: materialDetail?.partNumber || EMPTY_STRING,
            isPriceBook: !!materialDetail?.priceBook,
            businessPriceBook: materialDetail?.priceBook?.name || EMPTY_STRING,
            isTaxable: materialDetail?.isTaxable || false,
            materialImage: materialDetail?.materialImage
              ? {
                  url: materialDetail?.materialImage,
                }
              : {},
            categoryId: materialDetail?.category?.id || EMPTY_STRING,
            cost: materialDetail?.cost ? String(materialDetail?.cost) : EMPTY_STRING,
          };

          setInitialData(newData);
          reset(newData);
        })
        .catch((_error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, reset]
  );

  const handleGetListCategory = useCallback(
    (params: IQueryGetListCategory) => {
      setIsLoadingCategory(true);

      dispatch(getListCategory(params))
        .unwrap()
        .then((res) => {
          if (!res?.data) return;

          const { responses } = res?.data;
          const dataCategory: IBaseOption[] = responses.map((item) => ({
            value: item?.id,
            label: item?.name || EMPTY_STRING,
          }));

          setDataListCategory(dataCategory);
        })
        .catch((_error) => {})
        .finally(() => {
          setIsLoadingCategory(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    if (!materialId) return;

    handleGetMaterialDetail(materialId);
  }, [materialId, handleGetMaterialDetail]);

  useEffect(() => {
    const params: IQueryGetListCategory = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      type: TypeMasterDataEnum.CATEGORY_MATERIAL,
      ...(debounceCategorySearchKey && { textSearch: debounceCategorySearchKey }),
    };

    handleGetListCategory(params);
  }, [debounceCategorySearchKey, handleGetListCategory]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitForm = (data: IFormMaterial) => {
    if (data?.isPriceBook) {
      setIsShowPriceBookModal(true);
      return;
    }

    const newData: IFormMaterial = {
      ...data,
      quantity: data.quantity || String(DEFAULT_NUMBER_ZERO),
      price: data.price || String(DEFAULT_NUMBER_ZERO),
      cost: data.cost || String(DEFAULT_NUMBER_ZERO),
    };
    if (materialId) {
      handleEditMaterial(newData);
    } else {
      handleAddMaterial(newData);
    }
  };

  const handelSubmitFormPriceBook = (data: IFormPriceBookName) => {
    const dataForm: IFormMaterial = {
      ...getValues(),
      businessPriceBook: data.businessPriceBook,
      businessPriceBookType: data.businessPriceBookType,
    };

    if (materialId) {
      handleEditMaterial(dataForm);
    } else {
      handleAddMaterial(dataForm);
    }
  };

  const handleAddMaterial = async (data: IFormMaterial) => {
    setIsLoading(true);

    const body: IFormMaterial = removeEmptyObjects(data) as IFormMaterial;
    delete body.isPriceBook;

    const formData = await createFormData(body, 'materialImage');

    dispatch(createMaterial(formData))
      .unwrap()
      .then(() => {
        handleCancelForm();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditMaterial = async (data: IFormMaterial) => {
    const { isPriceBook, ...rest } = data;
    const dataConvert: IFormMaterial = {
      ...rest,
      // isUnSavePriceBook: !isPriceBook,
    };

    const dataChange: IFormMaterial = compareDataUpdate(initialData, dataConvert) as IFormMaterial;

    if (!materialId || !dataChange) return;

    const formatData: IFormMaterial = {
      ...dataChange,
      ...(dataChange.materialImage && isEmptyObject(dataChange.materialImage)
        ? { materialImage: undefined, deleteAvatar: true }
        : { materialImage: dataChange.materialImage }),
    };

    const formData = await createFormData(formatData, 'materialImage');

    setIsLoading(true);

    dispatch(updateMaterial({ materialId, body: formData }))
      .unwrap()
      .then((_res) => {
        handleCancelForm();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancelForm = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset(defaultValuesForm);
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleCloseModal = () => {
    setIsShowPriceBookModal(false);
  };

  const handleSearchCategoryChange = (searchKey: string) => {
    setCategorySearchKey(searchKey);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formMaterialPage'
      title={t(materialId ? 'form_material_edit_title' : 'form_material_add_title')}
      onBackToPreviousPage={handleCancelForm}
    >
      <Stack
        component={'form'}
        spacing={'20px'}
        onSubmit={handleSubmit(handleSubmitForm)}
        className={cx('form')}
      >
        <Box component={'div'} className={cx('formBody')}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
              <Grid2 size={12}>
                <Controller
                  name='materialImage'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FileUpload
                      accept={FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE}
                      label={t('form_material_label_image')}
                      {...(materialId && !value?.file && { uploadedFiles: value?.url })}
                      onChange={(files: IFileUpload[]) => onChange(files?.[DEFAULT_NUMBER_ZERO])}
                      errorMessage={errors.materialImage?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='name'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_material_label_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.name?.message}
                      required
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='partNumber'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_material_label_part_number')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.partNumber?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='description'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    multiline
                    height={'auto'}
                    rows={6}
                    label={t('form_material_label_description')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.description?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='cost'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_material_label_cost')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.cost?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='price'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_material_label_price')}
                      value={value}
                      onChange={(event) => {
                        const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                        const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                        onChange(number);
                      }}
                      errorMessage={errors?.price?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='unit'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t('form_material_label_unit')}
                      value={value}
                      options={mockDataMaterialUnit || []}
                      onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                      errorMessage={errors.unit?.message}
                      required
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='quantity'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_material_label_quantity')}
                      value={value}
                      onChange={(event) => {
                        onChange(formatInputDecimal(event.target.value));
                      }}
                      errorMessage={errors?.quantity?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='categoryId'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t('form_material_label_category')}
                      value={value}
                      options={dataListCategory || []}
                      onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                      errorMessage={errors.categoryId?.message}
                      onSearchChange={handleSearchCategoryChange}
                      isLoading={isLoadingCategory}
                      required
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Box flex={1}>
              <Stack direction='column'>
                <Controller
                  name='isTaxable'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      className={cx('formControlLabel')}
                      slotProps={{
                        typography: {
                          className: cx('checkboxLabel'),
                        },
                      }}
                      control={
                        <Checkbox
                          className={cx('checkbox')}
                          checked={value}
                          onChange={(event) => onChange(event.target.checked)}
                        />
                      }
                      label={t('form_material_label_taxable')}
                    />
                  )}
                />

                {/* <Controller
                  name='isPriceBook'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      className={cx('formControlLabel')}
                      slotProps={{
                        typography: {
                          className: cx('checkboxLabel'),
                        },
                      }}
                      control={
                        <Checkbox
                          className={cx('checkbox')}
                          checked={value}
                          onChange={(event) => onChange(event.target.checked)}
                        />
                      }
                      label={t('form_material_label_save_to_price_book')}
                    />
                  )}
                /> */}
              </Stack>
            </Box>
          </Grid2>
        </Box>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={handleCancelForm}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={materialId ? !isDirty : false}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>

        {isLoading && <Loading />}

        {isShowPriceBookModal && (
          <PriceBookModal
            businessPriceBook={businessPriceBook}
            handleSubmitForm={handelSubmitFormPriceBook}
            onClose={handleCloseModal}
          />
        )}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </Stack>
    </ChildrenPage>
  );
};

export default FormMaterial;
